import { store, store$ } from '@redux/store';
import { map, filter, distinctUntilChanged, switchMap, catchError, of, shareReplay } from 'rxjs';
import LoadingStatus from 'enums/loadingStatus';
import getArticleCountResults$ from '../apis/getArticleCountResults';
import blogSearchResultsSlice from '../redux/slices';
import { selectCountsResultsModifiers, selectArticlesCountsSearchCommand } from '../redux/selectors';

const { actions } = blogSearchResultsSlice;

/**
 * Observable that listens for changes in the Redux store and emits when the article counts are being fetched.
 *
 * This observable:
 * - Maps the Redux state to the `CountsResultsModifiers`.
 * - Filters for states where the `fetchingArticlesCountsStatus` is set to `Loading`.
 * - Ensures distinct emissions using `distinctUntilChanged`.
 *
 * @type {Observable<Object>}
 */
const onloadingArticleCountResults$ = store$.pipe(
    map(selectCountsResultsModifiers),
    distinctUntilChanged(),
    filter(({ fetchingArticlesCountsStatus }) =>
        fetchingArticlesCountsStatus.name === LoadingStatus.Loading.toString()
    ),
);

/**
 * Observable that fetches the article count results when triggered by `onloadingArticleCountResults$`.
 *
 * This observable:
 * - Switches to a new observable that fetches the article counts from the API.
 * - Dispatches success or error actions to the Redux store based on the API response.
 * - Caches the latest result using `shareReplay(1)` for reuse.
 *
 * @type {Observable<number>}
 */
const onArticleCountResultsLoaded$ = onloadingArticleCountResults$.pipe(
    switchMap(() => {
        const state = store.getState();
        const searchParameters = selectArticlesCountsSearchCommand(state);
        return getArticleCountResults$(searchParameters).pipe(
            switchMap((response) => {
                const countResults = Number(response);
                store.dispatch(actions.fetchArticlesCountsSuccess());
                return of(countResults);
            }),
            catchError((error) => {
                store.dispatch(actions.fetchArticlesCountsError(error));
                return of(0);
            })
        );
    }),
    shareReplay(1)
);

export default onArticleCountResultsLoaded$;